import React from "react";
import Contact from "../components/AboutUs/Contact";
import Founder from "../components/AboutUs/Founder";
import Content from "../components/Common/Content";
import Layout from "../components/Common/Layout";
import Main from "../components/Common/Main";
import Paragraph from "../components/Common/Paragrpah";
import Section from "../components/Common/Section";
import Subtitle from "../components/Common/Subtitle";
import Title from "../components/Common/Title";
import Spacing from "../constants/Spacing";
import { founders } from "../data/aboutUsData";
import SEO from "../components/Common/SEO";

const AboutUs = () => {
    return (
        <Layout title="About LearnCMD - Our Mission to Make Terminal Learning Accessible">
            <SEO 
                title="About LearnCMD - Our Mission to Make Terminal Learning Accessible"
                description="Learn about LearnCMD's mission to make Linux and Unix terminal learning accessible through interactive, hands-on tutorials. Meet our team and discover our vision for command line education."
                pathname="/about-us"
            />
            <Main>
                <Section>
                    <Content>
                        <Title
                            marginTop={Spacing.SPACING_3}
                            marginBottom={Spacing.SPACING_3}
                        >
                            About LearnCMD
                        </Title>
                        <Subtitle marginBottom={Spacing.SPACING_2}>
                            Contact Us
                        </Subtitle>
                        <Paragraph marginBottom={Spacing.SPACING_2}>
                            We would love to hear from you, so feel free to
                            contact us for any reason at:
                        </Paragraph>
                        <Contact />
                        <Subtitle marginBottom={Spacing.SPACING_2}>
                            Our Motivation
                        </Subtitle>
                        <Paragraph marginBottom={Spacing.SPACING_3}>
                            Over the years as a software developer and DevOps engineer, I've noticed a common pattern: <b>many skilled developers lack confidence with the Linux command line</b>. It's often <b>not a matter of capability, but of exposure</b>. They simply never had the chance—or the necessity—to develop real proficiency.
                            <br />
                            <br />
                            Most developers are familiar with the basics: navigating directories, copying files, editing text, and running simple commands. However, many—especially those who are self-taught—<b>feel uneasy around the terminal</b>. It can seem cryptic, unfriendly, or even obsolete, so they tend to avoid it.
                            <br />
                            <br />
                            But today, the command line is more relevant than ever. With the rise of cloud computing, Docker, and Kubernetes, <b>developers interact daily with powerful tools that rely on the terminal</b>. Writing code alone is no longer enough. Modern developers are expected to deploy, troubleshoot, and manage complex, distributed environments. <b>Command line proficiency is now a foundational skill</b> for working effectively with machines and infrastructure.
                            <br />
                            <br />
                            There's an abundance of resources online—videos, blog posts, tutorials—that cover Linux and the command line. Many are excellent. But watching someone demonstrate commands is not the same as building true fluency through hands-on experience. <b>Real learning happens by doing</b>—with guidance every step of the way.
                        </Paragraph>
                        <Subtitle marginBottom={Spacing.SPACING_2}>
                            Our Product
                        </Subtitle>
                        <Paragraph marginBottom={Spacing.SPACING_3}>
                            This platform is designed from the ground up to deliver an exceptional, <b>hands-on learning experience</b>. The environment launches in seconds. If something breaks, resetting it is effortless. There's <b>no setup, no configuration</b>—just start learning.
                            <br />
                            <br />
                            What sets it apart is the interactive guidance. The platform responds to the student's input, tracks their progress, and offers feedback in real time. Tasks can be completed in various ways, and the system intelligently recognizes them all.
                            <br />
                            <br />
                            <b>It offers depth when needed, but doesn't overwhelm</b>. Students can choose how much additional information they want to explore. Lessons are built to be fast, practical, and flexible, with detailed explanations always just a click away.
                        </Paragraph>
                        <Subtitle marginBottom={Spacing.SPACING_2}>
                            Our Vision
                        </Subtitle>
                        <Paragraph marginBottom={Spacing.SPACING_3}>
                            Our vision is to create a <b>best-in-class platform for learning Linux and the command line—a single, powerful destination for mastering these essential skills</b>. This is a tailor-made solution with one clear goal: to be the most effective and accessible interactive learning tool on the market. Whether you're a beginner or brushing up your skills, this platform empowers you to learn quickly, confidently, and thoroughly.
                        </Paragraph>
                    </Content>
                </Section>
                <Section marginBottom={Spacing.SPACING_7}>
                    <Content>
                        <Title marginBottom={Spacing.SPACING_3}>Founder</Title>
                        {founders.map(
                            ({ name, role, description, image }, index) => {
                                return (
                                    <Founder
                                        key={index}
                                        name={name}
                                        role={role}
                                        image={image}
                                        description={description}
                                    />
                                );
                            }
                        )}
                    </Content>
                </Section>
            </Main>
        </Layout>
    );
};

export default AboutUs;
